import React, { useEffect, useState } from 'react';
import {
  Modal,
  Box,
  Grid,
  Typography,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  TextField,
  Switch,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Close, ExpandMore, Edit, Delete, Add } from '@material-ui/icons';
import SearchSelect from 'react-select';
import service from '../../../apis/service';
import { apiEndpoints } from '../../../constants/apiEndpoints';
import { ErrorToast, notifySuccess } from '../../utils/Utils';

const useStyles = makeStyles({
  header: {
    borderBottom: '1px solid #eee',
    padding: '12px 20px',
    backgroundColor: '#fff',
    borderRadius: '8px 8px 0 0',
  },
  content: {
    padding: '16px 20px',
    maxHeight: '60vh',
    overflowY: 'auto',
    backgroundColor: '#fff',
  },
  partnerName: {
    fontWeight: 600,
    fontSize: '14px',
  },
  field: {
    fontSize: '13px',
    color: 'rgba(0,0,0,0.6)',
    marginBottom: '4px',
  },
  fieldValue: {
    fontWeight: 500,
    color: '#000',
  },
  emptyText: {
    textAlign: 'center',
    color: 'rgba(0,0,0,0.4)',
    padding: '32px 0',
  },
  actionBtn: {
    padding: '4px',
  },
  addBtn: {
    color: '#622FEE',
    borderColor: '#622FEE',
    '&:hover': { backgroundColor: 'rgba(98,47,238,0.06)' },
  },
});

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 620,
  backgroundColor: '#fff',
  borderRadius: '8px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
  outline: 'none',
};

const EMPTY_CREATE = { rockedNetworkPartnerId: '', externalId: '', additionalInfo: '', enabled: true };

const DealershipMetaModal = ({ isOpen, handleClose, dealershipId, dealershipName, dealershipCode }) => {
  const classes = useStyles();
  const [metaData, setMetaData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState('panel-0');

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [additionalInfoError, setAdditionalInfoError] = useState('');

  // Delete state
  const [deletingItem, setDeletingItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Create state
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [createErrors, setCreateErrors] = useState({});
  const [creating, setCreating] = useState(false);
  const [partners, setPartners] = useState([]);

  const fetchMeta = async () => {
    const code = dealershipCode ?? dealershipId;
    if (!code) return;
    setLoading(true);
    setMetaData([]);
    try {
      const response = await service.post(apiEndpoints.DEALERSHIP_META, { code });
      const items = response?.meta ?? response?.result?.meta ?? response?.data ?? [];
      setMetaData(items);
    } catch (error) {
      ErrorToast(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPartners = async () => {
    try {
      const response = await service.get(apiEndpoints.CERT_RNP_LIST);
      setPartners(response || []);
    } catch (error) {
      ErrorToast(error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchMeta();
      fetchPartners();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ── Edit ──────────────────────────────────────────────────────────────────

  const handleEditClick = (e, item) => {
    e.stopPropagation();
    setEditingId(item.id);
    setEditForm({
      externalId: item.externalId ?? '',
      inviteCode: item.inviteCode ?? '',
      enabled: item.enabled ?? false,
      additionalInfo: item.additionalInfo ? JSON.stringify(item.additionalInfo, null, 2) : '',
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({});
    setAdditionalInfoError('');
  };

  const handleSaveEdit = async (item) => {
    let parsedAdditionalInfo = null;
    if (editForm.additionalInfo) {
      try {
        parsedAdditionalInfo = JSON.parse(editForm.additionalInfo);
      } catch {
        setAdditionalInfoError('Invalid JSON');
        return;
      }
    }
    setSaving(true);
    try {
      await service.post(apiEndpoints.UPDATE_DEALERSHIP_META, {
        id: item.id,
        ...editForm,
        additionalInfo: parsedAdditionalInfo,
      });
      notifySuccess('Meta updated successfully');
      setEditingId(null);
      fetchMeta();
    } catch (error) {
      ErrorToast(error);
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────

  const handleDeleteClick = (e, item) => {
    e.stopPropagation();
    setDeletingItem(item);
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    try {
      await service.post(apiEndpoints.DELETE_DEALERSHIP_META, { id: deletingItem.id });
      notifySuccess('Meta deleted successfully');
      setDeletingItem(null);
      fetchMeta();
    } catch (error) {
      ErrorToast(error);
    } finally {
      setDeleting(false);
    }
  };

  // ── Create ────────────────────────────────────────────────────────────────

  const handleOpenCreate = () => {
    setCreateForm(EMPTY_CREATE);
    setCreateErrors({});
    setShowCreate(true);
  };

  const handleCreateSave = async () => {
    const errors = {};
    if (!createForm.rockedNetworkPartnerId) errors.rockedNetworkPartnerId = 'Required';
    if (!createForm.externalId.trim()) errors.externalId = 'Required';

    let parsedAdditionalInfo = null;
    if (createForm.additionalInfo) {
      try {
        parsedAdditionalInfo = JSON.parse(createForm.additionalInfo);
      } catch {
        errors.additionalInfo = 'Invalid JSON';
      }
    }

    if (Object.keys(errors).length) {
      setCreateErrors(errors);
      return;
    }

    setCreating(true);
    try {
      await service.post(apiEndpoints.CREATE_DEALERSHIP_META, {
        dealershipId,
        rockedNetworkPartnerId: createForm.rockedNetworkPartnerId,
        externalId: createForm.externalId,
        enabled: createForm.enabled,
        ...(parsedAdditionalInfo ? { additionalInfo: parsedAdditionalInfo } : {}),
      });
      notifySuccess('Meta created successfully');
      setShowCreate(false);
      fetchMeta();
    } catch (error) {
      ErrorToast(error);
    } finally {
      setCreating(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Modal open={isOpen} onClose={handleClose}>
        <Box style={modalStyle}>
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            className={classes.header}
          >
            <Grid item>
              <Typography style={{ fontWeight: 600, fontSize: '16px', color: '#000' }}>
                Meta — {dealershipName || `Dealership #${dealershipId}`}
              </Typography>
              <Typography style={{ fontSize: '12px', color: 'rgba(0,0,0,0.5)', marginTop: 2 }}>
                ID: {dealershipId}
              </Typography>
            </Grid>
            <Grid item>
              <Grid container alignItems="center" spacing={1}>
                <Grid item>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    className={classes.addBtn}
                    onClick={handleOpenCreate}
                  >
                    Add Meta
                  </Button>
                </Grid>
                <Grid item>
                  <Close onClick={handleClose} style={{ cursor: 'pointer', color: '#000' }} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <div className={classes.content}>
            {loading && (
              <Grid container justifyContent="center" style={{ padding: '32px 0' }}>
                <CircularProgress size={24} />
              </Grid>
            )}

            {!loading && metaData.length === 0 && (
              <Typography className={classes.emptyText}>No meta data available for this dealership.</Typography>
            )}

            {!loading &&
              metaData.map((item, index) => (
                <Accordion
                  key={item?.id ?? index}
                  expanded={expanded === `panel-${index}`}
                  onChange={(e, isExpanded) => setExpanded(isExpanded ? `panel-${index}` : false)}
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Grid container alignItems="center" justifyContent="space-between" wrap="nowrap">
                      <Grid item>
                        <Grid container alignItems="center" spacing={1}>
                          {item?.rockedNetworkPartner?.icon && (
                            <Grid item>
                              <img
                                src={item.rockedNetworkPartner.icon}
                                alt=""
                                style={{ width: 24, height: 24, borderRadius: '50%' }}
                              />
                            </Grid>
                          )}
                          <Grid item>
                            <Typography className={classes.partnerName}>
                              {item?.rockedNetworkPartner?.name ?? `Meta #${item?.id}`}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item>
                        <IconButton className={classes.actionBtn} onClick={(e) => handleEditClick(e, item)} size="small">
                          <Edit fontSize="small" style={{ color: '#622FEE' }} />
                        </IconButton>
                        <IconButton className={classes.actionBtn} onClick={(e) => handleDeleteClick(e, item)} size="small">
                          <Delete fontSize="small" style={{ color: '#d32f2f' }} />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </AccordionSummary>

                  <AccordionDetails>
                    {editingId === item.id ? (
                      <Grid container direction="column" spacing={2}>
                        <Grid item>
                          <TextField
                            label="External ID"
                            value={editForm.externalId}
                            onChange={(e) => setEditForm((p) => ({ ...p, externalId: e.target.value }))}
                            fullWidth size="small" variant="outlined"
                          />
                        </Grid>
                        <Grid item>
                          <TextField
                            label="Invite Code"
                            value={editForm.inviteCode}
                            onChange={(e) => setEditForm((p) => ({ ...p, inviteCode: e.target.value }))}
                            fullWidth size="small" variant="outlined"
                          />
                        </Grid>
                        <Grid item>
                          <TextField
                            label="Additional Info (JSON)"
                            value={editForm.additionalInfo}
                            onChange={(e) => {
                              setAdditionalInfoError('');
                              setEditForm((p) => ({ ...p, additionalInfo: e.target.value }));
                            }}
                            fullWidth size="small" variant="outlined" multiline minRows={3}
                            error={!!additionalInfoError}
                            helperText={additionalInfoError || 'Enter valid JSON or leave empty'}
                          />
                        </Grid>
                        <Grid item>
                          <Grid container alignItems="center" spacing={1}>
                            <Grid item><Typography style={{ fontSize: '13px' }}>Enabled</Typography></Grid>
                            <Grid item>
                              <Switch
                                checked={editForm.enabled}
                                onChange={(e) => setEditForm((p) => ({ ...p, enabled: e.target.checked }))}
                                color="primary" size="small"
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item>
                          <Grid container spacing={1}>
                            <Grid item>
                              <Button variant="contained" color="primary" size="small" disabled={saving} onClick={() => handleSaveEdit(item)}>
                                {saving ? 'Saving…' : 'Save'}
                              </Button>
                            </Grid>
                            <Grid item>
                              <Button size="small" onClick={handleCancelEdit}>Cancel</Button>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    ) : (
                      <Grid container direction="column" spacing={1}>
                        <Grid item>
                          <Typography className={classes.field}>
                            External ID: <span className={classes.fieldValue}>{item?.externalId ?? '—'}</span>
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Typography className={classes.field}>
                            Invite Code: <span className={classes.fieldValue}>{item?.inviteCode ?? '—'}</span>
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Typography className={classes.field}>
                            Enabled: <span className={classes.fieldValue}>{item?.enabled ? 'Yes' : 'No'}</span>
                          </Typography>
                        </Grid>
                        {item?.additionalInfo && (
                          <Grid item style={{ maxWidth: '100%', overflow: 'hidden' }}>
                            <Typography className={classes.field}>Additional Info:</Typography>
                            <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-all', background: '#f5f5f5', borderRadius: 4, padding: '6px 8px', maxHeight: 120, overflowY: 'auto' }}>
                              {JSON.stringify(item.additionalInfo, null, 2)}
                            </pre>
                          </Grid>
                        )}
                        <Grid item>
                          <Typography className={classes.field}>
                            Created:{' '}
                            <span className={classes.fieldValue}>
                              {item?.createdAt ? new Date(item.createdAt).toLocaleString() : '—'}
                            </span>
                          </Typography>
                        </Grid>
                      </Grid>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
          </div>
        </Box>
      </Modal>

      {/* Delete confirmation */}
      <Dialog open={!!deletingItem} onClose={() => setDeletingItem(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Meta</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>{deletingItem?.rockedNetworkPartner?.name ?? `Meta #${deletingItem?.id}`}</strong>? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingItem(null)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="secondary" disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create dialog */}
      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Meta</DialogTitle>
        <DialogContent>
          <Grid container direction="column" spacing={2} style={{ marginTop: 4 }}>
            <Grid item>
              <Typography style={{ fontSize: '12px', color: 'rgba(0,0,0,0.55)', marginBottom: 4 }}>
                Network Partner *
              </Typography>
              <SearchSelect
                options={partners.map((p) => ({ value: p.id, label: p.name }))}
                value={partners.map((p) => ({ value: p.id, label: p.name })).find((o) => o.value === createForm.rockedNetworkPartnerId) ?? null}
                onChange={(opt) => {
                  setCreateErrors((p) => ({ ...p, rockedNetworkPartnerId: '' }));
                  setCreateForm((p) => ({ ...p, rockedNetworkPartnerId: opt ? opt.value : '' }));
                }}
                placeholder="Select network partner"
                isClearable
                styles={{
                  control: (base, state) => {
                    let borderColor = '#ccc';
                    if (createErrors.rockedNetworkPartnerId) borderColor = '#f44336';
                    else if (state.isFocused) borderColor = '#622FEE';
                    return {
                      ...base,
                      minHeight: 40,
                      fontSize: 14,
                      borderColor,
                      boxShadow: state.isFocused ? '0 0 0 1px #622FEE' : 'none',
                      '&:hover': { borderColor: '#622FEE' },
                    };
                  },
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                  option: (base, state) => {
                    let backgroundColor = '#fff';
                    if (state.isSelected) backgroundColor = '#622FEE';
                    else if (state.isFocused) backgroundColor = '#f3eeff';
                    return {
                      ...base,
                      textAlign: 'left',
                      backgroundColor,
                      color: state.isSelected ? '#fff' : '#000',
                    };
                  },
                }}
              />
              {createErrors.rockedNetworkPartnerId && (
                <Typography style={{ fontSize: '12px', color: '#f44336', marginTop: 4 }}>
                  {createErrors.rockedNetworkPartnerId}
                </Typography>
              )}
            </Grid>
            <Grid item>
              <TextField
                label="External ID *"
                value={createForm.externalId}
                onChange={(e) => {
                  setCreateErrors((p) => ({ ...p, externalId: '' }));
                  setCreateForm((p) => ({ ...p, externalId: e.target.value }));
                }}
                fullWidth size="small" variant="outlined"
                error={!!createErrors.externalId}
                helperText={createErrors.externalId}
              />
            </Grid>
            <Grid item>
              <TextField
                label="Additional Info (JSON)"
                value={createForm.additionalInfo}
                onChange={(e) => {
                  setCreateErrors((p) => ({ ...p, additionalInfo: '' }));
                  setCreateForm((p) => ({ ...p, additionalInfo: e.target.value }));
                }}
                fullWidth size="small" variant="outlined" multiline minRows={3}
                error={!!createErrors.additionalInfo}
                helperText={createErrors.additionalInfo || 'Enter valid JSON or leave empty'}
              />
            </Grid>
            <Grid item>
              <Grid container alignItems="center" spacing={1}>
                <Grid item><Typography style={{ fontSize: '13px' }}>Enabled</Typography></Grid>
                <Grid item>
                  <Switch
                    checked={createForm.enabled}
                    onChange={(e) => setCreateForm((p) => ({ ...p, enabled: e.target.checked }))}
                    color="primary" size="small"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)} disabled={creating}>Cancel</Button>
          <Button onClick={handleCreateSave} color="primary" variant="contained" disabled={creating}>
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DealershipMetaModal;
